import Image from 'next/image'

import { useState } from 'react'
import { BusFront, Car, CarFront } from 'lucide-react'
import { cn } from '@/lib/utils'
import { normalizeText } from '@/lib/text'
import { isOptimizableImage } from '@/lib/images'

interface VehicleImageProps {
    src: string | null
    alt: string
    sizes: string
    categoria?: string
    preload?: boolean
    className?: string
}

const PlaceholderIcon = ({ categoria }: { categoria?: string }) => {
    const value = normalizeText(categoria ?? '')

    if (value.includes('van') || value.includes('minibus') || value.includes('bus')) return <BusFront className='size-6' aria-hidden='true' />
    if (value.includes('suv') || value.includes('todoterreno')) return <CarFront className='size-6' aria-hidden='true' />

    return <Car className='size-6' aria-hidden='true' />
}

// Fills its positioned parent. Without a photo (or if it fails to load) it shows an elegant placeholder.
export default function VehicleImage({ src, alt, sizes, categoria, preload = false, className }: VehicleImageProps) {
    const [failedSrc, setFailedSrc] = useState<string | null>(null)

    if (!src || failedSrc === src) {
        return (
            <div
                role='img'
                aria-label={`${alt}. Foto próximamente`}
                className='absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[radial-gradient(120%_90%_at_50%_0%,var(--card)_0%,var(--secondary)_55%,var(--muted)_100%)] text-gold'
            >
                <span className='flex size-14 items-center justify-center rounded-full border border-gold/25 bg-card/70 shadow-sm'>
                    <PlaceholderIcon categoria={categoria} />
                </span>

                <span className='text-[0.65rem] font-semibold uppercase tracking-[0.28em] text-muted-foreground'>
                    Foto próximamente
                </span>
            </div>
        )
    }

    return (
        <Image
            src={src}
            alt={alt}
            fill
            sizes={sizes}
            preload={preload}
            unoptimized={!isOptimizableImage(src)}
            onError={() => setFailedSrc(src)}
            className={cn('object-cover', className)}
        />
    )
}
